(() => {
  'use strict';
  if (window.FDSizeCalculator) { window.FDSizeCalculator.init(); return; }
  const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  const sizeKey = value => normalize(value).toUpperCase().replace(/\s+/g, '').replace(/^XXXL$/, '3XL').replace(/^XXXXL$/, '4XL').replace(/^XXL$/, '2XL');
  const cache = new Map();
  function loadCharts(url) {
    if (!cache.has(url)) cache.set(url, fetch(url, { credentials: 'same-origin' }).then(r => { if (!r.ok) throw new Error('charts'); return r.json(); }).catch(error => { cache.delete(url); throw error; }));
    return cache.get(url);
  }
  function chartKey(product, version) {
    const tags = product.tags || [], text = normalize([product.title, ...tags].join(' '));
    const tagKeys = new Set(tags.map(normalize));
    const explicit = tags.map(t => String(t).match(/^fd-sizechart:([a-z_]+)$/i)).find(Boolean)?.[1]?.toLowerCase();
    // Explicit chart tags are merchandising confirmations; do not infer a coat or a retro cut.
    if (explicit && !['fan', 'player'].includes(explicit)) return explicit;
    const kids = /\b(kids?|enfants?|junior|youth)\b/.test(text), women = /\b(women|femme|feminin)\b/.test(text), training = /\b(tracksuit|survetement|training suit|veste)\b/.test(text);
    const otherSport = /\b(nba|basketball|basket|nfl|nhl|mlb|baseball|hockey|rugby)\b/.test(text);
    const outerwear = /\b(windbreaker|coupe[ -]?vent|doudoune|coat|manteau)\b/.test(text);
    // The published women/kids football charts cannot stand in for other sports or outerwear.
    if ((kids || women) && (otherSport || outerwear || (women && training))) return null;
    if (/\b(baby|bebe)\b/.test(text)) return 'baby';
    if (kids) return training ? 'kids_tracksuit' : 'kids';
    if (women) return 'women';
    if (/\b(nba|basketball|basket)\b/.test(text)) return 'nba';
    if (/\b(nfl)\b/.test(text)) return 'nfl';
    if (/\b(nhl)\b/.test(text)) return 'nhl';
    if (/\b(mlb|baseball)\b/.test(text)) return 'mlb';
    if (/\b(rugby)\b/.test(text)) return 'rugby';
    if (/\b(hockey|doudoune|coat|manteau)\b/.test(text)) return null;
    if (/\b(windbreaker|coupe[ -]?vent)\b/.test(text)) return 'windbreaker';
    if (training) return 'tracksuit';
    if (/\b(polo)\b/.test(text)) return /\b(pants|pantalon)\b/.test(text) ? 'pants' : 'polo';
    const selected = normalize(version);
    if (/^(fan|standard|version fan|version standard)$/.test(selected)) return 'fan';
    if (/\b(player|pro|performance)\b/.test(selected)) return 'player';
    if (explicit) return explicit;
    if (/\b(player|version pro)\b/.test(text)) return 'player';
    if (/\b(retro|vintage|pantalon|short|ensemble)\b/.test(text)) return null;
    if (/\b(fan|supporter)\b/.test(text)) return 'fan';
    // Verified catalogue taxonomy for standalone adult jerseys. The generic Shopify
    // type "Maillot + short" is deliberately not used: it also labels single jerseys.
    if (tagKeys.has('maillot individuel') && (tagKeys.has('manches courtes') || tagKeys.has('manches longues'))) return 'fan';
    return null;
  }
  function aliases(chart, size) {
    const key = sizeKey(size), result = new Set([key]);
    if (chart === 'kids') {
      const sizes = ['16', '18', '20', '22', '24', '26', '28'], letters = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL'];
      const index = sizes.indexOf(key.split('(')[0]);
      if (index >= 0) { result.add(letters[index]); result.add(`${sizes[index]}(${letters[index]})`); }
    }
    if (['nfl', 'nhl'].includes(chart) && key.includes('/')) key.split('/').forEach(x => result.add(sizeKey(x)));
    return result;
  }
  function bodyMatches(chart, height, weight) {
    const heights = chart.reco?.taille || chart.reco?.enfant_cm || [];
    const weights = chart.reco?.poids || [];
    return heights.filter(([lo, hi, size]) => height >= lo && height <= hi && (!weights.length || weights.some(([wlo, whi, wsize]) => sizeKey(wsize) === sizeKey(size) && weight >= wlo && weight <= whi))).map(row => row[2]);
  }
  function garmentMatches(chart, width, length) {
    // This compares garments; it never turns a body circumference into a garment width.
    // A distant nearest neighbour is not accepted. Preserve actual source tolerances.
    const tolerance = chart.comparisonLimitCm || chart.toleranceCm || 2;
    return (chart.measurements || []).map(row => ({ ...row, dw: Math.abs(row.widthCm - width), dl: Math.abs(row.lengthCm - length) }))
      .filter(row => row.dw <= tolerance && row.dl <= tolerance)
      .sort((a, b) => (a.dw * a.dw + a.dl * a.dl) - (b.dw * b.dw + b.dl * b.dl))
      .map(row => row.size);
  }
  function boot(root) {
    const dialog = root.querySelector('[data-size-calculator]');
    if (!dialog || dialog.dataset.ready) return;
    dialog.dataset.ready = '1';
    const $ = s => dialog.querySelector(s), $$ = s => [...dialog.querySelectorAll(s)];
    const product = JSON.parse($('[data-fdsc-product]').textContent), labels = JSON.parse($('[data-fdsc-labels]').textContent);
    const si = Number(root.dataset.sizeIndex), vi = Number(root.dataset.versionIndex);
    const inputs = () => [...root.querySelectorAll(`.fds-option[data-option-index="${si}"] input[type="radio"]`)];
    const version = () => root.querySelector(`.fds-option[data-option-index="${vi}"] input:checked`)?.value || '';
    let charts, chart, key, method = 'body', previousOverflow, opener, run = 0;
    const result = $('[data-fdsc-result]');
    function clearResult() { result.hidden = true; result.replaceChildren(); }
    function message(text) { result.replaceChildren(); const p = document.createElement('p'); p.textContent = text; result.append(p); result.hidden = false; result.classList.add('is-empty'); }
    function availableFor(size) {
      const options = aliases(key, size);
      return inputs().find(input => !input.disabled && options.has(sizeKey(input.value)));
    }
    function selectMethod(next) {
      method = next;
      $$('[data-fdsc-method]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.fdscMethod === method)));
      $('[data-fdsc-body]').hidden = method !== 'body'; $('[data-fdsc-garment]').hidden = method !== 'garment';
      const weights = chart?.reco?.poids || [];
      $('[data-fdsc-height]').required = method === 'body'; $('[data-fdsc-height]').disabled = method !== 'body';
      $('[data-fdsc-weight]').required = method === 'body' && weights.length > 0; $('[data-fdsc-weight]').disabled = method !== 'body' || !weights.length;
      $('[data-fdsc-width]').required = method === 'garment'; $('[data-fdsc-width]').disabled = method !== 'garment';
      $('[data-fdsc-length]').required = method === 'garment'; $('[data-fdsc-length]').disabled = method !== 'garment';
      clearResult();
    }
    function table() {
      const wrap = $('[data-fdsc-table]'), table = document.createElement('table'), head = document.createElement('thead'), body = document.createElement('tbody');
      const headerRow = document.createElement('tr');
      [labels.size, ...chart.sizes].forEach((text, index) => { const th = document.createElement('th'); th.textContent = text; th.scope = 'col'; headerRow.append(th); });
      head.append(headerRow); table.append(head);
      chart.rows.forEach(row => { const tr = document.createElement('tr'), th = document.createElement('th'); th.scope = 'row'; th.textContent = row.k; tr.append(th); row.v.forEach(text => { const td = document.createElement('td'); td.textContent = text; tr.append(td); }); body.append(tr); });
      table.append(body); wrap.replaceChildren(table);
      // Source provenance stays in the data file; no wholesale destination in the purchase flow.
      $('[data-fdsc-note]').textContent = `${chart.note} ${labels.retailAvailability}`;
    }
    function configure() {
      key = chartKey(product, version()); chart = charts?.[key]; clearResult();
      $('[data-fdsc-context]').textContent = chart ? `${product.title} · ${chart.titre}` : product.title;
      $('[data-fdsc-loading]').hidden = true;
      $('[data-fdsc-content]').hidden = !chart; $('[data-fdsc-empty]').hidden = Boolean(chart);
      if (!chart) { $('[data-fdsc-empty]').textContent = labels.unknown; return; }
      const hasBody = Boolean((chart.reco?.taille || chart.reco?.enfant_cm || []).length), hasGarment = Boolean(chart.measurements?.length);
      $('[data-fdsc-method="body"]').disabled = !hasBody; $('[data-fdsc-method="garment"]').disabled = !hasGarment;
      $('[data-fdsc-weight-wrap]').hidden = !chart.reco?.poids?.length;
      $('[data-fdsc-body-intro]').textContent = chart.reco?.poids?.length ? labels.bodyIntro : labels.heightOnly;
      selectMethod(method === 'garment' && hasGarment ? 'garment' : hasBody ? 'body' : 'garment');
      $('[data-fdsc-form]').hidden = !hasBody && !hasGarment;
      if (!hasBody && !hasGarment) message(labels.tableOnly);
      table();
    }
    async function open(button) {
      if (dialog.open || si < 0) return;
      opener = button;
      const currentRun = ++run;
      previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      try { dialog.showModal(); } catch (_) { document.body.style.overflow = previousOverflow; return; }
      $('[data-fdsc-loading]').hidden = false; $('[data-fdsc-content]').hidden = true; $('[data-fdsc-empty]').hidden = true;
      $('[data-fdsc-loading]').textContent = labels.loading;
      $('[data-fdsc-context]').textContent = product.title;
      try { charts = await loadCharts(dialog.dataset.chartsUrl); if (currentRun === run && dialog.open) configure(); }
      catch (_) { if (currentRun !== run || !dialog.open) return; $('[data-fdsc-loading]').hidden = true; $('[data-fdsc-empty]').hidden = false; $('[data-fdsc-empty]').textContent = labels.error; }
    }
    root.querySelectorAll('[data-size-finder-open]').forEach(button => button.addEventListener('click', () => open(button)));
    $('[data-fdsc-close]').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', event => { if (event.target !== dialog) return; const bounds = dialog.getBoundingClientRect(); if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close(); });
    dialog.addEventListener('close', () => { run++; document.body.style.overflow = previousOverflow || ''; opener?.focus({ preventScroll: true }); });
    $$('[data-fdsc-method]').forEach(button => button.addEventListener('click', () => selectMethod(button.dataset.fdscMethod)));
    $$('input').forEach(input => input.addEventListener('input', clearResult));
    root.addEventListener('change', event => { if (event.target.closest('.fds-option') && dialog.open && charts) configure(); });
    $('[data-fdsc-form]').addEventListener('submit', event => {
      event.preventDefault(); if (!chart) return;
      const height = Number($('[data-fdsc-height]').value), weight = Number($('[data-fdsc-weight]').value), width = Number($('[data-fdsc-width]').value), length = Number($('[data-fdsc-length]').value);
      if (method === 'body' && (!height || (chart.reco?.poids?.length && !weight))) return message(labels.enterBody);
      if (method === 'garment' && (!width || !length)) return message(labels.enterMeasures);
      const matches = method === 'body' ? bodyMatches(chart, height, weight) : garmentMatches(chart, width, length);
      if (!matches.length) return message(method === 'body' ? labels.noBodyMatch : labels.noMeasureMatch);
      const available = matches.map(size => ({ size, input: availableFor(size) })).filter(item => item.input);
      if (!available.length) return message(labels.unavailable);
      // On measurement mode, show only the best compatible match, never an unavailable fallback.
      const choices = method === 'garment' ? available.slice(0, 1) : available;
      const heading = document.createElement('h3'); heading.textContent = labels.resultTitle;
      const basis = document.createElement('p'); basis.textContent = method === 'garment' ? labels.measureBasis.replace('__TOLERANCE__', String(chart.comparisonLimitCm || chart.toleranceCm || 2)) : chart.reco?.poids?.length ? labels.bodyBasis : labels.heightBasis;
      const actions = document.createElement('div'); actions.className = 'fdsc-result-actions';
      choices.forEach(({ size, input }) => {
        const button = document.createElement('button'); button.type = 'button'; button.textContent = labels.choose.replace('__SIZE__', input.value);
        button.addEventListener('click', () => {
          const target = availableFor(size); if (!target) { message(labels.unavailable); return; }
          target.checked = true; target.dispatchEvent(new Event('change', { bubbles: true })); dialog.close(); target.focus({ preventScroll: true });
        }); actions.append(button);
      });
      result.replaceChildren(heading, basis, actions); result.classList.remove('is-empty'); result.hidden = false;
    });
  }
  function init() { document.querySelectorAll('[data-fd-studio]').forEach(boot); }
  window.FDSizeCalculator = { init, chartKey, bodyMatches, garmentMatches, sizeKey };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', init) : init();
  document.addEventListener('shopify:section:load', init);
})();
